import React from 'react';
import { Building2, Flag, HeartHandshake, Globe2 } from 'lucide-react';

// Categorias com cor e ícone distintos
const TYPE_STYLES = {
  gov: { color: '#0F5A34', icon: Building2, label: 'Órgão Oficial' },
  consulate: { color: '#E53935', icon: Flag, label: 'Consulado' },
  ngo: { color: '#DFAF3A', icon: HeartHandshake, label: 'ONG / Apoio' },
  intl: { color: '#0F5A34', icon: Globe2, label: 'Organização Internacional' },
};

const INSTITUTIONS = [
  {
    name: 'AIMA',
    full: 'Agência para a Integração, Migrações e Asilo',
    url: 'https://aima.gov.pt',
    type: 'gov',
    desc: 'Órgão oficial português para vistos, autorizações de residência e nacionalidade.',
  },
  {
    name: 'CNAIM / CLAIM',
    full: 'Rede de Centros de Apoio ao Migrante',
    url: 'https://aima.gov.pt/pt/viver/rede-claim',
    type: 'gov',
    desc: 'Apoio gratuito presencial em todo o país: documentação, integração e direitos.',
  },
  {
    name: 'Consulado do Brasil em Lisboa',
    full: 'Consulado-Geral do Brasil em Lisboa',
    url: 'https://www.gov.br/mre/pt-br/consulado-lisboa',
    type: 'consulate',
    desc: 'Documentação consular brasileira para residentes na zona Centro-Sul.',
  },
  {
    name: 'Consulado do Brasil no Porto',
    full: 'Consulado-Geral do Brasil no Porto',
    url: 'https://www.gov.br/mre/pt-br/consulado-porto',
    type: 'consulate',
    desc: 'Atendimento consular para residentes no Norte e Centro de Portugal.',
  },
  {
    name: 'Consulado do Brasil em Faro',
    full: 'Consulado-Geral do Brasil em Faro',
    url: 'https://www.gov.br/mre/pt-br/consulado-faro',
    type: 'consulate',
    desc: 'Serviços consulares para brasileiros residentes no Algarve.',
  },
  {
    name: 'Casa do Brasil de Lisboa',
    full: 'Casa do Brasil de Lisboa',
    url: 'https://casadobrasildelisboa.pt',
    type: 'ngo',
    desc: 'Associação histórica de defesa dos direitos e integração dos brasileiros.',
  },
  {
    name: 'Cáritas Portuguesa',
    full: 'Cáritas Portuguesa',
    url: 'https://caritas.pt',
    type: 'ngo',
    desc: 'Apoio social, humanitário e jurídico a imigrantes e refugiados em Portugal.',
  },
  {
    name: 'JRS Portugal',
    full: 'Serviço Jesuíta aos Refugiados',
    url: 'https://jrsportugal.pt',
    type: 'ngo',
    desc: 'Acolhimento, acompanhamento e advocacia junto de migrantes vulneráveis.',
  },
  {
    name: 'CPR',
    full: 'Conselho Português para os Refugiados',
    url: 'https://cpr.pt',
    type: 'ngo',
    desc: 'Assistência jurídica gratuita a refugiados e requerentes de asilo.',
  },
  {
    name: 'OIM Portugal',
    full: 'Organização Internacional para as Migrações',
    url: 'https://portugal.iom.int',
    type: 'intl',
    desc: 'Agência da ONU para migrações: programas de retorno, integração e proteção.',
  },
];

const SupportInstitutions = () => (
  <section className="py-12 md:py-16 bg-[#F5F0E8]" data-testid="institutions-section">
    <div className="container mx-auto px-4">
      <div className="text-center max-w-2xl mx-auto mb-10">
        <span className="inline-block bg-[#0F5A34] text-white font-['Montserrat'] text-[11px] uppercase tracking-[0.1em] px-3 py-1 rounded-sm mb-3">
          Apoio Institucional
        </span>
        <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl font-bold text-[#1A1A1A] mb-3 leading-tight">
          Quem apoia os imigrantes em Portugal
        </h2>
        <p className="text-[#5C5C5C] font-['Inter'] text-base leading-relaxed">
          Instituições oficiais, consulados e organizações de referência que oferecem
          informação, apoio jurídico e acolhimento a quem chega ao país.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {INSTITUTIONS.map((inst) => {
          const style = TYPE_STYLES[inst.type];
          const Icon = style.icon;
          return (
            <a
              key={inst.name}
              href={inst.url}
              target="_blank"
              rel="noopener noreferrer"
              className="group bg-white border border-[#D6D2C4] rounded-sm p-5 flex flex-col gap-3 hover:border-[#0F5A34] hover:shadow-md transition-all"
              data-testid={`institution-${inst.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div
                  className="w-11 h-11 rounded-sm flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: `${style.color}15`, color: style.color }}
                >
                  <Icon size={22} />
                </div>
                <span
                  className="text-[10px] font-['Montserrat'] uppercase tracking-[0.06em] font-medium px-2 py-1 rounded-sm flex-shrink-0"
                  style={{ backgroundColor: `${style.color}15`, color: style.color }}
                >
                  {style.label}
                </span>
              </div>
              <div>
                <h3 className="font-['Playfair_Display'] text-lg font-semibold text-[#1A1A1A] leading-tight group-hover:text-[#0F5A34] transition-colors">
                  {inst.name}
                </h3>
                <p className="text-[11px] text-[#5C5C5C] font-['Inter'] mt-0.5">
                  {inst.full}
                </p>
              </div>
              <p className="text-sm text-[#3F3F3F] font-['Inter'] leading-relaxed flex-1">
                {inst.desc}
              </p>
              <div className="flex items-center gap-1.5 text-xs font-['Inter'] text-[#0F5A34] font-medium group-hover:underline pt-1 border-t border-[#E9E5D6]">
                <span>{new URL(inst.url).hostname.replace(/^www\./, '')}</span>
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                  <path d="M7 17L17 7M9 7h8v8" />
                </svg>
              </div>
            </a>
          );
        })}
      </div>

      <p className="text-center text-xs text-[#5C5C5C] font-['Inter'] mt-8 italic">
        Lista curada de instituições oficiais — Portugal à Vista não tem relação comercial com nenhuma delas.
      </p>
    </div>
  </section>
);

export default SupportInstitutions;
